<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>
    <div class="sl-order-form__lines">
        <transition-group name="list" tag="div">
            <template
                v-for="(line, index) in lines"
                :key="line.code"
            >
                <component
                    :is="line.component"
                    :code="line.code"
                    :line="line"
                    :index="index + 1"
                    @ungroup="ungroup"
                    @templated="onTemplated"
                />
            </template>
        </transition-group>
    </div>
</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>
import { groupBy, sortBy, uniqBy } from 'lodash-es';
import GroupedFormLine from './GroupedFormLine.vue';
import SplittedFormLine from './SplittedFormLine.vue';
import NoFormLine from './NoFormLine.vue';
import SectionFormLine from './SectionFormLine.vue';
import InlineFormLine from './InlineFormLine.vue';
import Parameter from '@/models/Parameter';
import Arrays from '@/utils/Arrays';
import FormPrototype from '@/models/FormPrototype';

const COPY_PARAMETERS_PCODE = 'COPY_PARAMETERS';
const CUSTOMER_PRICE = 'CUSTOMER_PRICE';
const SPECIAL_PRICE = 'SPECIAL_PRICE';
const DELETE_BUTTON = 'DELETE_BUTTON';

/** @type {string[]} These parameters will be rendered without a form line. */
const NO_LINE_PARAMETERS = [
    'PLOT_GRAPH_BUTTON',
    'RESET_BACK_SURFACE_BUTTON',
    'CONVERT_TO_LIBERTY_BUTTON',
];

export default
{
    name: 'LensOrderForm',

    props:
    {
        sectionParameters:
        {
            type:    Array,
            default: () => [],
        },

        section:
        {
            type:    [String, null],
            default: null,
        },
    },

    emits: ['templated'],

    data()
    {
        return {
            splitFieldCodes: [], // Fields (codes) that must be split (asked by user)
        };
    },

    computed:
    {
        lines()
        {
            const lines = [];

            /** @type {?FormPrototype} */
            const rightPrototype    = this.$store.getters['lensOrder/getPrototype']('right');
            /** @type {?FormPrototype} */
            const leftPrototype     = this.$store.getters['lensOrder/getPrototype']('left');
            const allParameters     = []; // all ungrouped parameters from left & right prototypes (*without* section & inline ones)
            const sectionParameters = []; // all *section* parameters from left & right prototypes
            const inlineParameters  = []; // all *inline* parameters from left & right prototypes
            const noLineParameters  = []; // all "out-of-flow" parameters from left & right prototypes

            if(this.sectionParameters.length)
            {
                // The `section-parameters` prop is set: use those.
                // Happens when this component is nested in a `SectionFormLine`.
                allParameters.push(...this.sectionParameters);
            }
            else
            {
                // The `section-parameters` prop is NOT set: use those from the prototypes.
                // Default behavior.
                if(rightPrototype)
                {
                    allParameters.push(...rightPrototype.getParametersAsArray());
                }

                if(leftPrototype)
                {
                    allParameters.push(...leftPrototype.getParametersAsArray());
                }
            }

            // Keep only not "HIDDEN" parameters, unique by code and sort by parameter's sort order
            const allVisibleParameters = sortBy(
                uniqBy(
                    allParameters.filter(
                        parameter => parameter.form_display !== 'HIDDEN'
                    ),
                    parameter => parameter.code
                ),
                parameter => parameter.sort_order
            );

            // Move all "section" parameters to handle them separately
            sectionParameters.push(...Arrays.removeIf(allVisibleParameters, p => p.section !== null));

            // Move all "inline" parameters to handle them separately
            inlineParameters.push(...Arrays.removeIf(allVisibleParameters, p => p.line !== null));

            // Some specific "custom" parameters are rendered differently, out
            // of the form's normal flow. Let's handle them separately.
            noLineParameters.push(...Arrays.removeIf(allVisibleParameters, p => NO_LINE_PARAMETERS.includes(p.code)));

            // Build "splitted" and "grouped" form lines
            for(const parameter of allVisibleParameters)
            {
                const rightParameter = rightPrototype ? rightPrototype.getParameterByCode(parameter.code) : null;
                const leftParameter  = leftPrototype  ? leftPrototype.getParameterByCode(parameter.code)  : null;
                let component = SplittedFormLine;

                if(rightParameter && leftParameter && !this.splitFieldCodes.includes(parameter.code))
                {
                    // The current parameter exists on both sides and isn't explicitly split.

                    // Are both sides grouped? (Y=Yes or F=Forced)
                    if((['Y', 'F']).includes(rightParameter.is_grouped) && (['Y', 'F']).includes(leftParameter.is_grouped))
                    {
                        // Group parameters if both the grouping keys and the values are identical
                        const groupingKeysMatch = rightParameter.grouping_key === leftParameter.grouping_key;
                        const valuesMatch = rightParameter.getValue() === leftParameter.getValue();
                        if(groupingKeysMatch && valuesMatch)
                        {
                            component = GroupedFormLine;
                        }
                    }
                }

                lines.push({
                    code:    parameter.code,
                    rightParameter,
                    leftParameter,
                    section: this.section,
                    component, // either SplittedFormLine or GroupedFormLine
                });
            }

            // Build "section" form lines
            const sectionParameterLines = Object.entries(groupBy(sectionParameters, 'section'))
                .map(([section, params]) =>
                {
                    const rightParams = rightPrototype ? params.map(p => rightPrototype.getParameterByCode(p.code)) : [];
                    const leftParams  = leftPrototype  ? params.map(p => leftPrototype.getParameterByCode(p.code))  : [];

                    return {
                        code:           section,
                        rightParameter: rightParams.filter(p => p),
                        leftParameter:  leftParams.filter(p => p),
                        section,
                        component:      SectionFormLine,
                    };
                });

            // Build "inline" form lines
            const inlineParameterLines = Object.entries(groupBy(inlineParameters, 'line'))
                .map(([line, params]) =>
                {
                    const rightParams = rightPrototype ? params.map(p => rightPrototype.getParameterByCode(p.code)) : [];
                    const leftParams  = leftPrototype  ? params.map(p => leftPrototype.getParameterByCode(p.code))  : [];

                    return {
                        code:           line,
                        rightParameter: rightParams.filter(p => p),
                        leftParameter:  leftParams.filter(p => p),
                        line,
                        component:      InlineFormLine,
                    };
                });

            // Build "no line" form lines
            const noLineParameterLines = noLineParameters.map(param =>
            {
                const rightParameter = rightPrototype ? rightPrototype.getParameterByCode(param.code) : null;
                const leftParameter  = leftPrototype  ? leftPrototype.getParameterByCode(param.code)  : null;

                return {
                    code:      param.code,
                    rightParameter,
                    leftParameter,
                    component: NoFormLine,
                };
            });

            lines.push(
                ...sectionParameterLines,
                ...inlineParameterLines,
                ...noLineParameterLines
            );

            // Add new line (specific to SL6)
            if(!this.inSection)
            {
                const protos = {
                    right: rightPrototype,
                    left:  leftPrototype,
                };
                this.addCopyParametersLine(lines, protos);
                this.addPriceLine(lines, rightPrototype, leftPrototype);
                this.addSpecialPriceLine(lines, rightPrototype, leftPrototype);
                this.addDeleteButtonLine(lines, rightPrototype, leftPrototype);
            }

            // Sort all lines
            lines.sort((a, b) => this.getSortOrder(a) - this.getSortOrder(b));

            return lines;
        },

        inSection()
        {
            return this.sectionParameters.length > 0;
        },
    },

    methods:
    {
        ungroup(code)
        {
            this.splitFieldCodes.push(code);
        },

        onTemplated({ side })
        {
            // Refresh graphs if auto-plot is enabled
            if(this.$store.state.lensOrder.fittings.autoplot[side])
            {
                let plotGraphButtonLine = null;
                for(let i = 0; i < this.lines.length; i++)
                {
                    const line = this.lines[i];
                    if(line.code === 'PLOT_GRAPH_BUTTON')
                    {
                        plotGraphButtonLine = line;
                        break;
                    }
                }

                if(plotGraphButtonLine === null)
                {
                    // The "Plot Graph" button was not found; we are probably
                    // within a section and thus only have access to the params
                    // of this section.
                    // Let's bubble up the "templated" event so that the parent
                    // FormLines component may catch it.
                    this.$emit('templated', { side, bubbledUp: true });
                }
                else
                {
                    // There is a "Plot Graph" button, so let's plot.
                    this.$store.dispatch('lensOrder/plot', side);
                }
            }
        },

        /**
         * Get the sort order of a form line, based on the associated parameter.
         * If the line contains multiple parameters (i.e. an array) on either
         * side, then only the first one is considered.
         *
         * @param {Object} line
         * @param {Object|Object[]} line.rightParameter
         * @param {Object|Object[]} line.leftParameter
         * @returns {number}
         */
        getSortOrder(line)
        {
            const rightParameter = Array.isArray(line.rightParameter)
                ? line.rightParameter[0]
                : line.rightParameter;

            const leftParameter = Array.isArray(line.leftParameter)
                ? line.leftParameter[0]
                : line.leftParameter;

            return (rightParameter || leftParameter).sort_order;
        },

        /**
         * Add a button to copy fitting values from one side to the other.
         *
         * @param {Object[]} lines  The form lines.
         * @param {Object}   protos The prototypes.
         * @param {FormPrototype} [protos.right]
         * @param {FormPrototype} [protos.left]
         */
        addCopyParametersLine(lines, protos)
        {
            if(!protos.right || !protos.left)
            {
                return;
            }

            const o = {
                code:      COPY_PARAMETERS_PCODE,
                component: SplittedFormLine,
            };

            for(const side of ['right', 'left'])
            {
                o[`${side}Parameter`] = new Parameter({
                    code:         COPY_PARAMETERS_PCODE,
                    form_display: 'CUSTOM',
                    form_label:   this.$t('parameters.COPY_PARAMETERS.label'),
                    has_access:   true,
                    is_enabled:   true,
                    sort_order:   1,
                });
            }

            if(o.rightParameter || o.leftParameter)
            {
                lines.unshift(o);
            }
        },

        /**
         * Add the "Customer Price" info field when editing an order (review).
         *
         * @param {Object[]} lines The form lines.
         * @param {?FormPrototype} rightPrototype
         * @param {?FormPrototype} leftPrototype
         */
        addPriceLine(lines, rightPrototype, leftPrototype)
        {
            if(
                !this.$route.path.includes('edit') ||
                !this.$store.getters['account/can']('price:view')
            )
            {
                return;
            }

            const customerPriceParameter = new Parameter({
                code:                CUSTOMER_PRICE,
                force_defined_value: false,
                defined_value:       null,
                form_display:        'CUSTOM',
                form_label:          this.$t('parameters.CUSTOMER_PRICE.label'),
                has_access:          true,
                is_enabled:          true,
                sort_order:          9992,
                value_type:          "float",
            });

            const o = {
                code:      CUSTOMER_PRICE,
                component: SplittedFormLine,
            };

            if(rightPrototype)
            {
                o.rightParameter = customerPriceParameter;
            }

            if(leftPrototype)
            {
                o.leftParameter = customerPriceParameter;
            }

            if(o.rightParameter || o.leftParameter)
            {
                lines.push(o);
            }
        },

        /**
         * Add the "Special Price" field when editing an order (review).
         *
         * @param {Object[]} lines The form lines.
         * @param {?FormPrototype} rightPrototype
         * @param {?FormPrototype} leftPrototype
         */
        addSpecialPriceLine(lines, rightPrototype, leftPrototype)
        {
            if(
                !this.$route.path.includes('edit') ||
                !this.$store.getters['account/can']('price:edit')
            )
            {
                return;
            }

            const specialPriceParameter = new Parameter({
                code:                SPECIAL_PRICE,
                force_defined_value: false,
                defined_value:       null,
                form_display:        'CUSTOM',
                form_label:          this.$t('parameters.SPECIAL_PRICE.label'),
                has_access:          true,
                is_enabled:          true,
                sort_order:          9994,
                value_type:          "float",
            });

            const o = {
                code:      SPECIAL_PRICE,
                component: SplittedFormLine,
            };

            if(rightPrototype)
            {
                o.rightParameter = specialPriceParameter;
            }

            if(leftPrototype)
            {
                o.leftParameter = specialPriceParameter;
            }

            if(o.rightParameter || o.leftParameter)
            {
                lines.push(o);
            }
        },

        /**
         * Add the "Delete" button when editing an order (review).
         *
         * @param {Object[]} lines The form lines.
         * @param {?FormPrototype} rightPrototype
         * @param {?FormPrototype} leftPrototype
         */
        addDeleteButtonLine(lines, rightPrototype, leftPrototype)
        {
            if(
                !this.$route.path.includes('edit') ||
                !this.$store.getters['account/isStaff']
            )
            {
                return;
            }

            const deleteButtonParameter = new Parameter({
                code:                DELETE_BUTTON,
                force_defined_value: false,
                defined_value:       null,
                form_display:        'CUSTOM',
                form_label:          null,
                has_access:          true,
                is_enabled:          true,
                sort_order:          9996,
                value_type:          null,
            });

            const o = {
                code:      DELETE_BUTTON,
                component: SplittedFormLine,
            };

            if(rightPrototype)
            {
                o.rightParameter = deleteButtonParameter;
            }

            if(leftPrototype)
            {
                o.leftParameter = deleteButtonParameter;
            }

            if(o.rightParameter || o.leftParameter)
            {
                lines.push(o);
            }
        },
    },
};
</script>

<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style scoped>
@reference "#main.css";

/* Transitions */
.list-enter-active,
.list-leave-active
{
    transition: all 0.5s ease;
}

.list-enter-to,
.list-leave-from
{
    @apply max-h-[1000px] opacity-100;
}

.list-enter-from,
.list-leave-to
{
    @apply max-h-0 opacity-0;
}
</style>
